import React, { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  Link,
} from '@mui/material';
import {
  SchoolOutlined,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useBranding } from '../contexts/BrandingContext';

/**
 * Identifica um ambiente de treinamento/teste.
 *
 * Mesma estrategia do getApiUrl() (services/api.ts): deriva de window.location em vez de cravar
 * valor por ambiente, com override explicito por env var. Consequencia importante: o selo NUNCA
 * aparece em producao, mesmo que este arquivo seja mergeado para o release.
 */
function isAmbienteTreinamento(): boolean {
  const override = process.env.REACT_APP_AMBIENTE;
  if (override) return override.toLowerCase() === 'treinamento';
  if (typeof window === 'undefined') return false;
  // Convencao de hostname da frota: <sistema>-tst.<dominio> (ex.: neogestor-tst.root.ooo).
  return /(^|[.-])tst([.-]|$)/.test(window.location.hostname);
}

const ModernLogin: React.FC = () => {
  const { login, isAuthenticated } = useAuth();
  const { branding } = useBranding();
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !password.trim()) return;
    setLoading(true);
    try {
      await login(email.trim(), password);
      setError('');
    } catch (err: any) {
      const msg = err.response?.data?.message || err.response?.data?.error;
      setError(msg && typeof msg === 'string' ? msg : 'Credenciais invalidas. Verifique seu e-mail e senha.');
    } finally {
      setLoading(false);
    }
  };

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  const nomePlataforma = branding.branding_nome || 'NeoGestor';
  const subtitulo = branding.branding_subtitulo || 'Plataforma de Governanca de Indicadores';
  const ambienteTreinamento = isAmbienteTreinamento();

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #f0f4ff 0%, #e8eeff 50%, #f8faff 100%)',
        px: 2,
        py: 4,
      }}
    >
      {/* Card central */}
      <Box
        sx={{
          width: '100%',
          maxWidth: 400,
          backgroundColor: '#fff',
          borderRadius: '16px',
          boxShadow: '0 4px 24px rgba(0, 50, 120, 0.08), 0 1px 4px rgba(0, 0, 0, 0.04)',
          border: '1px solid rgba(0, 122, 240, 0.08)',
          px: { xs: 3, sm: 5 },
          py: { xs: 4, sm: 5 },
        }}
      >
        {/* Logo + nome */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          {branding.branding_logo ? (
            <Box
              component="img"
              src={branding.branding_logo}
              alt={nomePlataforma}
              sx={{ width: 56, height: 56, objectFit: 'contain', borderRadius: '12px', mx: 'auto', mb: 1.5 }}
            />
          ) : (
            <Box
              sx={{
                width: 56,
                height: 56,
                mx: 'auto',
                mb: 1.5,
                borderRadius: '12px',
                background: 'linear-gradient(135deg, #007AF0, #0051A2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography sx={{ fontSize: '1.375rem', fontWeight: 700, color: 'white' }}>
                {nomePlataforma.substring(0, 2).toUpperCase()}
              </Typography>
            </Box>
          )}
          <Typography sx={{ fontSize: '1.375rem', fontWeight: 700, color: '#24252E', lineHeight: 1.2 }}>
            {nomePlataforma}
          </Typography>
          <Typography sx={{ fontSize: '0.8125rem', color: '#696979', mt: 0.5 }}>
            {subtitulo}
          </Typography>

          {/* Selo de ambiente: so aparece fora de producao (ver isAmbienteTreinamento).
              Ambar de propósito — o vermelho aqui seria lido como erro, e o Alert de
              credencial invalida logo abaixo ja usa essa cor. */}
          {ambienteTreinamento && (
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.75,
                mt: 2,
                px: 1.5,
                py: 0.625,
                borderRadius: '999px',
                backgroundColor: '#FFFBEB',
                border: '1px solid #FCD34D',
                color: '#92400E',
              }}
            >
              <SchoolOutlined sx={{ fontSize: '1rem' }} />
              <Typography
                component="span"
                sx={{
                  fontSize: '0.6875rem',
                  fontWeight: 700,
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  lineHeight: 1.4,
                  whiteSpace: 'nowrap',
                }}
              >
                Ambiente de Treinamento
              </Typography>
            </Box>
          )}
        </Box>

        {/* Erro */}
        {error && (
          <Alert
            severity="error"
            onClose={() => setError('')}
            sx={{
              mb: 2.5,
              borderRadius: '8px',
              backgroundColor: '#FEECED',
              border: '1px solid #FAC8C9',
              fontSize: '0.875rem',
              color: '#A4151E',
            }}
          >
            {error}
          </Alert>
        )}

        {/* Formulario */}
        <Box component="form" onSubmit={handleSubmit}>
          {/* E-mail */}
          <Box sx={{ mb: 2.5 }}>
            <Typography sx={{ fontSize: '0.8125rem', fontWeight: 500, color: '#515162', mb: 0.75 }}>
              E-mail
            </Typography>
            <TextField
              required
              fullWidth
              type="email"
              autoComplete="email"
              autoFocus
              placeholder="seu@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  fontSize: '0.875rem',
                  backgroundColor: '#FAFBFC',
                  '& fieldset': { borderColor: '#D3D4DD' },
                  '&:hover fieldset': { borderColor: '#007AF0' },
                  '&.Mui-focused fieldset': { borderColor: '#007AF0' },
                  '&.Mui-focused': { backgroundColor: '#fff' },
                },
              }}
            />
          </Box>

          {/* Senha */}
          <Box sx={{ mb: 1.5 }}>
            <Typography sx={{ fontSize: '0.8125rem', fontWeight: 500, color: '#515162', mb: 0.75 }}>
              Senha
            </Typography>
            <TextField
              required
              fullWidth
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              placeholder="Sua senha"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              size="small"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      size="small"
                      sx={{ color: '#AAAAB9' }}
                    >
                      {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  fontSize: '0.875rem',
                  backgroundColor: '#FAFBFC',
                  '& fieldset': { borderColor: '#D3D4DD' },
                  '&:hover fieldset': { borderColor: '#007AF0' },
                  '&.Mui-focused fieldset': { borderColor: '#007AF0' },
                  '&.Mui-focused': { backgroundColor: '#fff' },
                },
              }}
            />
          </Box>

          {/* Esqueceu a senha */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
            <Link
              component="button"
              type="button"
              onClick={() => navigate('/forgot-password')}
              sx={{
                fontSize: '0.8125rem',
                fontWeight: 500,
                color: '#007AF0',
                textDecoration: 'none',
                '&:hover': { color: '#0069D0', textDecoration: 'underline' },
              }}
            >
              Esqueceu a senha?
            </Link>
          </Box>

          {/* Botao entrar */}
          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={loading}
            sx={{
              py: 1.25,
              backgroundColor: '#007AF0',
              '&:hover': { boxShadow: 'none', backgroundColor: '#0069D0' },
              borderRadius: '8px',
              fontWeight: 600,
              fontSize: '0.875rem',
              boxShadow: 'none',
              '&:focus-visible': { outline: '2px solid #007AF0', outlineOffset: 2 },
            }}
          >
            {loading ? <CircularProgress size={22} color="inherit" /> : 'Entrar'}
          </Button>
        </Box>

        {/* Rodape */}
        <Typography sx={{ color: '#AAAAB9', fontSize: '0.6875rem', mt: 4, textAlign: 'center' }}>
          &copy; {new Date().getFullYear()} {nomePlataforma}. Todos os direitos reservados.
        </Typography>
      </Box>
    </Box>
  );
};

export default ModernLogin;
